/**
 * Confirm/cancel shell for destructive or consequential actions — extracted
 * from four near-identical hand-rolled Modal wrappers (RemoveMemberDialog,
 * RemoveWebsiteDialog, CalendarsPage/AlertsPage DeleteModal) that had all
 * converged on the same shape: featured icon + title + free-form body +
 * two-button footer that stacks full-width (confirm button first) under
 * 768px. Not in design_system_export's spec set — composed from Modal +
 * Button + Icon, same precedent as CalendarSelect/RadioCardGrid.
 *
 * Body content (typed-name confirmation fields, inline failure blocks) stays
 * caller-supplied via children — this component only owns the shell/footer.
 *
 * @param {boolean} open
 * @param {function} onClose - cancel
 * @param {function} onConfirm
 * @param {'destructive'|'warning'=destructive} variant - icon disc color + confirm button hierarchy
 * @param {ReactNode} icon - featured icon glyph
 * @param {ReactNode} title
 * @param {ReactNode=} children - body content
 * @param {string} confirmLabel
 * @param {string=Cancel} cancelLabel
 * @param {boolean=false} confirmDisabled - beyond `busy`, e.g. a typed-name gate
 * @param {boolean=false} busy - disables both buttons, shows a spinner on confirm
 * @param {string=} panelClassName - width override etc., passed to Modal's className
 */
import React from "react";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import styles from "./ConfirmDialog.module.scss";

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  variant = "destructive",
  icon,
  title,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  confirmDisabled = false,
  busy = false,
  panelClassName,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    variant={variant}
    icon={icon}
    title={title}
    className={panelClassName}
    footer={
      <div className={styles.footerButtons}>
        <Button
          className={styles.footerBtn}
          hierarchy="secondaryGray"
          onClick={onClose}
          disabled={busy}
        >
          {cancelLabel}
        </Button>
        <Button
          className={styles.footerBtn}
          hierarchy={variant === "warning" ? "primary" : "destructive"}
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
          loading={busy}
        >
          {confirmLabel}
        </Button>
      </div>
    }
  >
    {children}
  </Modal>
);

export default ConfirmDialog;
